import { useTranslation } from 'react-i18next';
import { scenes } from '@/config/siteImages';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';

export default function CreditSection() {
  const { t } = useTranslation();
  const isEs = i18n.language === 'es';

  const tips = [1, 2, 3].map((n) => ({
    icon: ['ri-hand-coin-line', 'ri-pie-chart-2-line', 'ri-arrow-up-circle-line'][n - 1],
    title: t(`sllc_credit_tip_${n}_title`),
    desc: t(`sllc_credit_tip_${n}_desc`),
  }));

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Content — first on mobile */}
          <div className="order-1 lg:order-2">
            <span className="text-[#4F2D63] text-sm font-semibold uppercase tracking-widest">
              {t('sllc_credit_eyebrow')}
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-5">
              {t('sllc_credit_title')}
            </h2>
            <p className="text-sm text-gray-500 font-medium mb-4">{t('sllc_credit_subtitle')}</p>

            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
              {t('sllc_credit_body_1')}
            </p>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
              {t('sllc_credit_body_2')}
            </p>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-7">
              {t('sllc_credit_body_3')}
            </p>

            {/* Tips */}
            <div className="space-y-3 mb-7">
              {tips.map((tip) => (
                <div key={tip.title} className="flex gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-xl border border-[#E9D9F2] bg-[#F8F3FB]">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-[#4F2D63] flex-shrink-0">
                    <i className={`${tip.icon} text-white text-sm`} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm mb-1">{tip.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to={isEs ? '/es/contacto' : '/contact'}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('sllc_credit_cta')}
            </Link>
          </div>

          {/* Right: Image — second on mobile */}
          <div className="order-2 lg:order-1 relative">
            <div className="w-full h-64 sm:h-80 lg:h-[480px] rounded-xl overflow-hidden">
              <img
                src={scenes.sllcCredit}
                alt="Business credit building strategy"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Credit score badge — repositioned for smaller screens */}
            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-white border border-[#E9D9F2] rounded-xl px-4 sm:px-5 py-3 sm:py-4 max-w-[200px] sm:max-w-[240px]">
              <div className="text-xs text-gray-500 mb-1">{t('sllc_credit_score_label')}</div>
              <div className="text-xl sm:text-2xl font-bold text-[#4F2D63] mb-1">680+</div>
              <div className="text-xs text-gray-600 leading-relaxed">{t('sllc_credit_score_ideal')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
